import type { ReactElement } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useController } from "react-hook-form"

import { AerosDatePicker, type AerosDatePickerProps } from "../AerosDatePicker"

export interface FormDatePickerProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>
  extends Omit<AerosDatePickerProps, "name" | "defaultValue" | "onChange" | "value"> {
  control: Control<TFieldValues>
  name: TName
  helperText?: string
  onChange?: (value: string | null) => void
  // Opt-in for dense layouts (e.g. a table-like row of many small fields) that surface a single
  // shared error banner elsewhere instead of per-field text — the red border still shows.
  hideErrorMessage?: boolean
}

export const FormDatePicker = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  onChange,
  helperText,
  hideErrorMessage,
  ...props
}: FormDatePickerProps<TFieldValues, TName>): ReactElement => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (value: string | null) => {
    field.onChange(value)
    onChange?.(value)
  }

  return (
    <AerosDatePicker
      {...field}
      {...props}
      error={!!error}
      helperText={hideErrorMessage ? helperText : (error?.message ?? helperText)}
      onChange={handleChange}
    />
  )
}
